'use client'

import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { ResearchTile } from '@/src/components/NewThreatResearch/NewThreatResearch.interface'
import { ResourceTile } from '@/src/components/ExploreResources/ExploreResources.interface'
import { ReportTile } from '@/src/components/IndustryReports/IndustryReports.interface'
import { GithubRepoTile } from '@/src/components/GithubRepos/GithubRepos.interface'
import { GithubRepoTileComponent } from '@/src/components/GithubRepos/GithubRepoTile'
import { MitreAttackTile } from '@/src/app/threat-library/[threatId]/MitreAttackTile.interface'
import { TileSwiperProps, TileVariant } from './TileSwiper.interface'
import { A11y, Keyboard, Pagination, FreeMode } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import '@/src/styles/overrides/swiper_overrides.scss'

import styles from './TileSwiper.module.scss'
import SwiperArrowDark from '@/public/assets/images/icon_swiper_arrow_prev_black.svg'
import SwiperArrowLight from '@/public/assets/images/icon_swiper_arrow_prev_white.svg'
import { Swiper as SwiperClass } from 'swiper/types'

function industryReportHref(ctaLink: string): string {
  return ctaLink.startsWith('http') ? ctaLink : `https://www.zscaler.com${ctaLink}`
}

const TileSwiper = <T extends TileVariant>({
  variant,
  data,
  externalStyles,
  wrapperClassName,
  customBreakpoints,
}: TileSwiperProps<T>): React.ReactElement => {
  const [tileSwiper, setTileSwiper] = useState<SwiperClass | null>(null)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [isClient, setIsClient] = useState(false) // safety check to make sure swiper is ready to init
  const [isSliderEnd, setIsSliderEnd] = useState(false)
  /** Arrow-step pagination: current snap / total snaps (not per-slide index). */
  const [pagerSnapCurrent, setPagerSnapCurrent] = useState(1)
  const [pagerSnapTotal, setPagerSnapTotal] = useState(1)

  const DARK_VARIANTS = ['industryReports', 'gitRepos', 'mitreAttack']
  // The type of 'variant' here is correctly inferred as T, which extends string
  const isDarkBackground = (currentVariant: T | undefined): boolean => {
    if (!currentVariant) return false
    // We must assert T as string for includes, or change DARK_VARIANTS to T[]
    return DARK_VARIANTS.includes(currentVariant as string)
  }
  const panelBackgroundIsDark = isDarkBackground(variant)

  // Calculate progress bar width
  const totalSlides = data.length
  const progressWidth = totalSlides > 0 ? 100 / totalSlides : 0
  const progressTransform = currentSlideIndex * progressWidth

  const syncPagerFromSwiper = (swiper: SwiperClass) => {
    const total = Math.max(1, swiper.snapGrid?.length ?? 1)
    const snapIndex = swiper.snapIndex ?? 0
    let current = snapIndex + 1
    // With slidesPerView: 'auto' and ~1.5 slides visible, snapIndex can lag isEnd/isBeginning by a frame or two.
    if (swiper.isEnd) {
      current = total
    } else if (swiper.isBeginning) {
      current = 1
    }
    setPagerSnapTotal(total)
    setPagerSnapCurrent(Math.min(total, Math.max(1, current)))
  }

  const handleSlideChange = (swiper: SwiperClass) => {
    const newIndex = swiper.realIndex
    setCurrentSlideIndex(newIndex)
    setIsSliderEnd(swiper.isEnd)
    syncPagerFromSwiper(swiper)
  }

  const handleSwiperProgress = (swiper: SwiperClass) => {
    setIsSliderEnd(swiper.isEnd)
    syncPagerFromSwiper(swiper)
  }

  const handleSwiperInit = (swiper: SwiperClass) => {
    setTileSwiper(swiper)
    setCurrentSlideIndex(swiper.realIndex)
    setIsSliderEnd(swiper.isEnd)
    syncPagerFromSwiper(swiper)
  }

  /** Re-read snap index after motion/layout (partial slides can leave snapIndex stale until transition end). */
  const handleSwiperLayoutSync = (swiper: SwiperClass) => {
    setIsSliderEnd(swiper.isEnd)
    syncPagerFromSwiper(swiper)
  }

  const handleArrowLeft = () => {
    tileSwiper?.slidePrev()
  }

  const handleArrowRight = () => {
    tileSwiper?.slideNext()
  }

  const formatSlideNumber = (n: number) => n.toString().padStart(2, '0')
  const isFirstSlide = currentSlideIndex === 0
  /** Matches next-arrow disabled state (swiper.isEnd), including grouped / free-mode layouts. */
  const isLastPage = isSliderEnd
  const progressLeft = isLastPage ? 100 - progressWidth : progressTransform

  // Set client state
  useEffect(() => {
    setIsClient(true)
  }, [])

  // Update Swiper on resize
  useEffect(() => {
    if (tileSwiper) {
      const handleResize = () => {
        tileSwiper.update()
        syncPagerFromSwiper(tileSwiper)
      }

      window.addEventListener('resize', handleResize)

      // Cleanup the event listener when the component unmounts
      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }
  }, [tileSwiper])

  // --- Default Swiper Breakpoint Configuration ---
  const defaultBreakpoints = {
    0: {
      slidesPerView: 'auto' as const,
      slidesPerGroup: 1,
      freeMode: { enabled: true, sticky: true },
    },
    768: {
      slidesPerView: 'auto' as const,
      slidesPerGroup: 1,
      freeMode: { enabled: true, sticky: true },
      pagination: false,
    },
    1024: {
      slidesPerView: 'auto' as const,
      slidesPerGroup: 1,
      pagination: false,
      navigation: false,
      allowTouchMove: false,
      freeMode: false,
    },
  }

  // Swiper Configuration
  const swiperParams = {
    spaceBetween: 20,
    loop: false,
    watchOverflow: true,
    centeredSlides: false,

    // Use customBreakpoints if available, otherwise use defaults
    breakpoints: customBreakpoints || defaultBreakpoints,
  }

  if (!isClient) {
    // this acts as placeholder for the module until data is avail in case of api call
    return (
      <div className={`${styles.sliderWrapper} ${wrapperClassName}`}>
        <div style={{ minHeight: '400px' }}>Loading...</div>
      </div>
    )
  }

  return (
    <>
      {/* --- Main Swiper / Grid --- */}
      <div className={`${styles.sliderWrapper} pb-[30px] ${wrapperClassName}`}>
        <Swiper
          modules={[A11y, Keyboard, Pagination, FreeMode]}
          onInit={handleSwiperInit}
          onSlideChange={handleSlideChange}
          onProgress={handleSwiperProgress}
          onSlideChangeTransitionEnd={handleSwiperLayoutSync}
          onTransitionEnd={handleSwiperLayoutSync}
          onSlidesUpdated={handleSwiperLayoutSync}
          {...swiperParams}
          className={styles.tileSwiper}
        >
          {variant &&
            variant === 'threatResearch' &&
            // Assert that 'data' is ResearchTile[] before mapping
            (data as ResearchTile[]).map((tile: ResearchTile) => (
              <SwiperSlide key={tile.id} className={`mr-0! w-[335px]!`}>
                <div
                  className={`
                  ${styles.tile}
                  ${styles.researchTile}
                  flex flex-col h-full bg-white w-full
                `}
                >
                  {/* image top */}
                  <div className="relative w-full pt-[56.25%] overflow-hidden">
                    <Image
                      src={tile.image}
                      alt={tile.imageAlt}
                      layout="fill"
                      objectFit="cover"
                      priority={tile.id <= 4}
                    />
                  </div>
                  {/* content & link section */}
                  <div
                    className={`${styles.contentWrapper} p-[15px] flex grow flex-col content-start gap-[10px]`}
                  >
                    <span
                      className={`
                        inline-block text-[10px] font-medium py-[5px] px-[10px] 
                        mb-[10px] bg-[#eceff2] tracking-[0.5px] w-fit
                      `}
                    >
                      {tile.category}
                    </span>
                    <h3
                      className={`
                      text-[20px] font-medium leading-[120%] mb-[20px]
                    `}
                    >
                      {tile.title}
                    </h3>
                    <Link
                      href={tile.ctaLink}
                      className={`${styles.ctaLink} no-underline text-[14px] font-medium inline-block tracking-[0.5px] w-fit mt-auto`}
                    >
                      {tile.ctaText}
                    </Link>
                  </div>
                  {/* author footer */}
                  <div className={styles.authorWrapper}>
                    <div className={styles.author}>
                      {tile?.authorImg && (
                        <div className={`${styles.authorAvatar} w-[54px]`}>
                          <Image
                            src={tile.authorImg}
                            alt={tile.author}
                            layout="fill"
                            objectFit="cover"
                            priority={tile.id <= 4}
                            className={styles.authorImg}
                          />
                        </div>
                      )}

                      <div className={styles.authorInfo}>
                        <span>{tile.author}</span>
                        <br />
                        <span className={styles.postDate}>
                          {tile.date}
                          {tile.readTime && `| ${tile.readTime}`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}

          {variant &&
            variant === 'exploreResources' &&
            // Assert that 'data' is ResourceTile[] before mapping
            (data as ResourceTile[]).map((tile: ResourceTile) => (
              <SwiperSlide key={tile.id} className={`mr-0! w-[335px]!`}>
                <div
                  className={`
                  ${styles.tile}
                  ${styles.variant}
                  flex flex-col h-full bg-white w-full min-h-[295px]
                `}
                >
                  {/* content & link section */}
                  <div
                    className={`${styles.contentWrapper} p-[30px] flex grow flex-col content-start gap-[10px]`}
                  >
                    <span className="flex items-center text-[10px] font-medium mb-[20px] tracking-[0.5px] w-fit">
                      <span className="inline-block w-[6px] h-[6px] align-middle mr-[10px] bg-[#ED1951]"></span>
                      {tile.type}
                    </span>
                    <h3 className="text-[16px] md:text-[20px] lg:text-[24px] font-medium leading-[120%] mb-[20px] font-gt-haptik">
                      {tile.title}
                    </h3>
                    <Link
                      href={tile.ctaLink}
                      className={`
                        ctaLink no-underline text-[14px] font-medium inline-block tracking-[0.5px] w-fit mt-auto
                        ${styles.ctaLink}
                      `}
                      {...(tile.ctaLink &&
                      (tile.ctaLink.startsWith('http://') || tile.ctaLink.startsWith('https://'))
                        ? { target: '_blank', rel: 'noopener noreferrer' }
                        : {})}
                    >
                      {tile.ctaText}
                    </Link>
                  </div>
                </div>
              </SwiperSlide>
            ))}

          {variant &&
            variant === 'industryReports' &&
            // Assert that 'data' is ReportTile[] before mapping
            (data as ReportTile[]).map((tile: ReportTile, index: number) => {
              const reportHref = industryReportHref(tile.ctaLink)
              return (
                <SwiperSlide key={index} className={`mr-0! w-[335px]!`}>
                  <div
                    className={`
                  ${styles.tile}
                  flex flex-col h-full bg-[#29333F] text-white 
                  // Adding arbitrary width/min-height for similar size control
                  min-h-[295px] relative overflow-hidden w-full
                `}
                  >
                    {/* Accent Corner (Pink Flag) */}
                    <div
                      className={`absolute top-0 right-0 w-[12px] h-[12px] bg-[#ED1951] ${styles.reportsAccentCorner}`}
                    ></div>

                    {/* Content Section */}
                    <div
                      className={`${styles.contentWrapper} p-5 flex grow flex-col content-start justify-between`}
                    >
                      <div className="flex flex-col h-full">
                        <span className="flex items-center text-[10px] font-medium mb-[20px] tracking-[0.5px] w-fit uppercase">
                          <span className="inline-block w-[6px] h-[6px] align-middle mr-[10px] bg-[#ED1951]"></span>
                          Threat Research
                        </span>
                        <span className="text-[12px] font-medium text-[#c0c0c0] tracking-[0.5px] block mb-2.5">
                          {tile.resourceType}
                        </span>
                        <h3 className="text-[24px] font-medium leading-[120%] mb-[15px]">
                          <Link
                            href={reportHref}
                            target="_blank"
                            className={`
                            ctaLink no-underline font-medium text-white! text-[16px] leading-[130%] border-0! hover:text-[#ED1951]!
                            ${styles.ctaLink}
                          `}
                          >
                            {tile.title}
                          </Link>
                        </h3>
                        <Link
                          href={reportHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${styles.industryReadMoreCta} font-dm-mono mt-auto!`}
                        >
                          READ MORE
                          <span className="sr-only"> about {tile.title}</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              )
            })}

          {variant === 'gitRepos' &&
            (data as GithubRepoTile[]).map((repo: GithubRepoTile) => (
              <SwiperSlide key={repo.id} className={`mr-0! w-[335px]!`}>
                <GithubRepoTileComponent data={repo} />
              </SwiperSlide>
            ))}
          {variant &&
            variant === 'mitreAttack' &&
            (data as MitreAttackTile[]).map(({ tactic, attacks }, index) => (
              <SwiperSlide key={index} className={styles.swiperSlide}>
                <div className={`${styles.tile} ${externalStyles?.mitreMatrixCard}`}>
                  <h3 className={externalStyles?.tactic}>{tactic.replace(/_/g, ' ')}</h3>
                  <div className={externalStyles?.attackItem}>{attacks.join(' / ')}</div>
                </div>
              </SwiperSlide>
            ))}
        </Swiper>
      </div>

      {/* Navigation Controls */}
      {data.length > 1 && (
        <div
          className={`${styles.slideControls} ${data.length > 4 ? 'lg:block' : 'lg:none'} ${panelBackgroundIsDark ? styles.darkTheme : styles.lightTheme}`}
        >
          <div aria-hidden={true} className={styles.progressBar}>
            <div
              className={styles.currentProgress}
              style={{
                width: `${progressWidth}%`,
                left: `${progressLeft}%`,
              }}
            ></div>
          </div>
          <div className={styles.controls}>
            <div className={styles.pagination}>
              <span>
                <span className={styles.activePage}>
                  {formatSlideNumber(pagerSnapCurrent)}
                </span>
                /{formatSlideNumber(pagerSnapTotal)}
              </span>
            </div>
            <div className={styles.navigation}>
              <button
                onClick={handleArrowLeft}
                className={`${styles.navButton} ${isFirstSlide ? styles.disabled : ''}`}
              >
                <Image
                  src={panelBackgroundIsDark ? SwiperArrowLight : SwiperArrowDark} //inverse Prev Arrow depends on darkBg
                  className={styles.prevArrow}
                  alt="Previous Slide"
                  style={{ opacity: isFirstSlide ? 0.2 : 1 }}
                />
              </button>
              <button
                onClick={handleArrowRight}
                className={`${styles.navButton} ${isLastPage ? styles.disabled : ''}`}
              >
                <Image
                  src={panelBackgroundIsDark ? SwiperArrowLight : SwiperArrowDark} //inverse Next Arrow depends on darkBg
                  className={styles.nextArrow}
                  alt="Next Slide"
                  style={{ opacity: isLastPage ? 0.2 : 1 }}
                />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default TileSwiper
